// components/EscalationReportsPage.js
// Reports for /escalations-claims/reports — 11 report types with filters,
// summary metrics computed over the complete dataset, and filtered xlsx export.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, DatePicker, Empty, Input, Select, Space, Table, Tag, message } from "antd";
import {
  ArrowLeftOutlined, DownloadOutlined, FileExcelOutlined, FilterOutlined, ReloadOutlined, SearchOutlined,
} from "@ant-design/icons";
import Cookies from "js-cookie";
import {
  ACTION_TYPES, CARRIERS, CHANNELS, REPORT_TYPES, STATUSES,
  downloadExport, errMsg, fetchOptions, fetchReport,
} from "../services/escalationService";
import "./EscalationsClaims.css";

const { RangePicker } = DatePicker;

const EMPTY_FILTERS = {
  escalationRange: null, carrier: null, actionType: null, status: null,
  escalatedVia: null, assignedTo: null, search: "",
};

const EscalationReportsPage = ({ darkTheme = false, isMobile = false }) => {
  const navigate = useNavigate();
  const token = Cookies.get("token") || undefined;

  const [reportType, setReportType] = useState("daily-followups");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    fetchOptions(token).then(setOptions).catch(() => {});
  }, [token]);

  const buildParams = useCallback(() => {
    const p = {};
    if (filters.escalationRange && filters.escalationRange[0]) {
      p.escalationFrom = filters.escalationRange[0].format("YYYY-MM-DD");
      p.escalationTo = filters.escalationRange[1] ? filters.escalationRange[1].format("YYYY-MM-DD") : undefined;
    }
    if (filters.carrier) p.carrier = filters.carrier;
    if (filters.actionType) p.actionType = filters.actionType;
    if (filters.status) p.status = filters.status;
    if (filters.escalatedVia) p.escalatedVia = filters.escalatedVia;
    if (filters.assignedTo) p.assignedTo = filters.assignedTo;
    if (filters.search) p.search = filters.search;
    return p;
  }, [filters]);

  const runReport = useCallback(async (type = reportType) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReport(token, type, buildParams());
      setReport(res);
    } catch (err) {
      setError(errMsg(err, "Failed to load report"));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [token, buildParams, reportType]);

  useEffect(() => { runReport(reportType); }, [reportType]);

  const columns = useMemo(() => {
    if (!report) return [];
    return (report.columns || []).map((c) => ({
      title: c.title,
      dataIndex: c.key,
      key: c.key,
      width: c.key === "latestRemarks" || c.key === "issueReason" || c.key === "resolutionRemarks" ? 220 : 130,
      ellipsis: true,
    }));
  }, [report]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport(
        token,
        `/api/escalations/reports/${reportType}/export`,
        buildParams(),
        `escalations-${reportType}-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      message.success("Report exported");
    } catch (err) {
      message.error(errMsg(err, "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount = useMemo(
    () => Object.entries(filters).filter(([, v]) => Boolean(v)).length,
    [filters]
  );

  const assigneeOpts = (() => {
    const base = [...(options?.assignees || [])];
    (options?.users || []).forEach((u) => { if (u.name && !base.includes(u.name)) base.push(u.name); });
    return base.map((n) => ({ label: n, value: n }));
  })();

  return (
    <div className={`ec-container ${darkTheme ? "ec-dark" : ""} ${isMobile ? "ec-mobile" : ""}`}>
      <div className="ec-hero">
        <div className="ec-hero-left">
          <span className="ec-hero-eyebrow">Analytics</span>
          <h1 className="ec-hero-title">Escalation Reports</h1>
          <span className="ec-hero-sub">Every metric and row below is computed over the complete filtered dataset.</span>
        </div>
        <div className="ec-hero-actions">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/escalations-claims")}>Register</Button>
          <Button icon={<ReloadOutlined />} onClick={() => runReport()} loading={loading}>Run Report</Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            loading={exporting}
            disabled={!report}
            onClick={handleExport}
          >
            Export Excel
          </Button>
        </div>
      </div>

      <div className="ec-filters">
        <div className="ec-filters-row">
          <Select
            value={reportType}
            onChange={setReportType}
            style={{ minWidth: 300 }}
            options={REPORT_TYPES.map((r) => ({ label: r.label, value: r.key }))}
          />
          <RangePicker
            value={filters.escalationRange}
            onChange={(v) => setFilters({ ...filters, escalationRange: v })}
            placeholder={["Date From", "Date To"]}
            allowClear
          />
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search order / AWB"
            allowClear
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onPressEnter={() => runReport()}
            style={{ minWidth: 200 }}
          />
        </div>
        <div className="ec-filters-row">
          <Select
            allowClear placeholder="Carrier" value={filters.carrier}
            onChange={(v) => setFilters({ ...filters, carrier: v || null })}
            options={CARRIERS.map((c) => ({ label: c, value: c }))}
            style={{ minWidth: 160 }}
          />
          <Select
            allowClear placeholder="Action Type" value={filters.actionType}
            onChange={(v) => setFilters({ ...filters, actionType: v || null })}
            options={ACTION_TYPES.map((c) => ({ label: c, value: c }))}
            style={{ minWidth: 150 }}
          />
          <Select
            allowClear placeholder="Status" value={filters.status}
            onChange={(v) => setFilters({ ...filters, status: v || null })}
            options={STATUSES.map((c) => ({ label: c, value: c }))}
            style={{ minWidth: 180 }}
          />
          <Select
            allowClear placeholder="Escalated Via" value={filters.escalatedVia}
            onChange={(v) => setFilters({ ...filters, escalatedVia: v || null })}
            options={CHANNELS.map((c) => ({ label: c, value: c }))}
            style={{ minWidth: 150 }}
          />
          <Select
            allowClear showSearch placeholder="Assigned To" value={filters.assignedTo}
            onChange={(v) => setFilters({ ...filters, assignedTo: v || null })}
            options={assigneeOpts}
            style={{ minWidth: 170 }}
          />
          <Space>
            <Button type="primary" icon={<FilterOutlined />} onClick={() => runReport()}>
              Apply{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
            <Button onClick={() => { setFilters(EMPTY_FILTERS); setTimeout(() => runReport(), 0); }}>Clear</Button>
          </Space>
        </div>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} action={<Button size="small" onClick={() => runReport()}>Retry</Button>} />}

      {report && (
        <>
          <div className="ec-report-head">
            <h2>{report.title}</h2>
            <Tag style={{ background: "#ecfdf5", color: "#0b5d3b", border: "none", borderRadius: 8 }}>
              {report.rowCount} rows
            </Tag>
          </div>
          <div className="ec-summary-grid">
            {(report.summary || []).map((s) => (
              <div key={s.label} className="ec-summary-card">
                <span className="ec-summary-label">{s.label}</span>
                <span className="ec-summary-value">{String(s.value)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="ec-register">
        <Table
          rowKey={(row, idx) => row.escalationId || row.salesOrderId || row.date || row.carrier || row.assignedTo || row.channel || idx}
          size="small"
          loading={loading}
          columns={columns}
          dataSource={report ? report.rows : []}
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100],
            showTotal: (t) => `${t} rows`,
          }}
          locale={{
            emptyText: <Empty description={loading ? "Loading…" : "No data for the selected report and filters"} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          }}
        />
      </div>
      <div className="ec-hint">
        <DownloadOutlined /> Exports include every row matching the active filters (not just the visible page).
      </div>
    </div>
  );
};

export default EscalationReportsPage;

